import { unref, ref, computed } from 'vue'
import { useForm } from 'vee-validate'
import type { ContactFormData } from './contact-form.types'
import { contactFormRules } from './contact-form.rules'
import { useContactFormShared } from './useContactFormShared'
import { useCountryStore } from '@/composables/geo/useCountryStore'
import { buildEmailFormData, runContactSubmitFlow } from './contact-form.helpers'

const INITIAL_VALUES = {
  fullName: '',
  email: '',
  locality: '',
  region: '',
  message: '',
  privacyAccepted: false,
}

export interface UseContactEmailFormOptions {
  requirePrivacyAccepted?: boolean
  showRegion?: boolean
  courseId?: string
}

const fieldOpts = { validateOnModelUpdate: false }

export function useContactEmailForm(options: UseContactEmailFormOptions = {}) {
  const { requirePrivacyAccepted = true, showRegion: showRegionProp = false, courseId } = options
  const shared = useContactFormShared({ showRegion: showRegionProp })
  const { countryIsoCode } = useCountryStore()
  const isoCode = computed(() => countryIsoCode.value ?? undefined)

  const validationSchema = {
    fullName: contactFormRules.fullName,
    email: contactFormRules.email,
    locality: () => true as const,
    region: (value: unknown) => (shared.showRegion.value ? contactFormRules.regionRequired(value) : true),
    message: contactFormRules.messageOptional,
    privacyAccepted: (value: unknown) =>
      requirePrivacyAccepted ? contactFormRules.privacyAccepted(value) : (true as const),
  }

  const { defineField, errors, validate, resetForm: veeResetForm, values } = useForm({
    validationSchema,
    initialValues: { ...INITIAL_VALUES },
    validateOnMount: false,
  })

  const [fullName, fullNameAttrs] = defineField('fullName', fieldOpts)
  const [email, emailAttrs] = defineField('email', fieldOpts)
  const [locality, localityAttrs] = defineField('locality', fieldOpts)
  const [region, regionAttrs] = defineField('region', fieldOpts)
  const [message, messageAttrs] = defineField('message', fieldOpts)
  const [privacyAccepted, privacyAcceptedAttrs] = defineField('privacyAccepted', fieldOpts)

  const resetForm = () => veeResetForm({ values: { ...INITIAL_VALUES } })
  const loading = ref(false)
  const submitError = ref<string | null>(null)

  const submitForm = async (
    onSuccess?: (data: ContactFormData) => void,
    onError?: (error: Error) => void,
    getErrorMessage?: (error: Error, statusCode?: number) => string
  ) => {
    await runContactSubmitFlow(
      validate,
      () => unref(values) as Record<string, unknown> | undefined,
      (v) =>
        buildEmailFormData(v, {
          showRegion: shared.showRegion.value,
          courseId,
          countryIso: isoCode.value,
        }),
      shared.submitToApi,
      {
        setLoading: (v) => { loading.value = v },
        setError: (v) => { submitError.value = v },
        resetForm,
      },
      { onSuccess, onError, getErrorMessage }
    )
  }

  return {
    fullName,
    fullNameAttrs,
    email,
    emailAttrs,
    locality,
    localityAttrs,
    region,
    regionAttrs,
    message,
    messageAttrs,
    privacyAccepted,
    privacyAcceptedAttrs,
    errors,
    showRegion: shared.showRegion,
    resetForm,
    submitForm,
    loading,
    submitError,
  }
}
